import type {AnalysisResponse} from './browserGameAnalysis';

const memoryCache = new Map<string, AnalysisResponse[]>();

function canUseLocalStorage(): boolean {
  // A presence check (`typeof localStorage !== 'undefined'`) is not enough:
  // Safari private mode exposes a localStorage object whose setItem throws
  // QuotaExceededError. Probe with an actual write/remove.
  try {
    if (typeof localStorage === 'undefined') {
      return false;
    }
    const probeKey = '__cc_ls_probe__';
    localStorage.setItem(probeKey, '1');
    localStorage.removeItem(probeKey);
    return true;
  } catch {
    return false;
  }
}

export async function getCached(
  key: string,
): Promise<AnalysisResponse[] | null> {
  const inMemory = memoryCache.get(key);
  if (inMemory) {
    return inMemory;
  }
  if (!canUseLocalStorage()) {
    return null;
  }
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return null;
    }
    memoryCache.set(key, parsed);
    return parsed;
  } catch {
    return null;
  }
}

export async function putCached(
  key: string,
  responses: AnalysisResponse[],
): Promise<void> {
  memoryCache.set(key, responses);
  if (!canUseLocalStorage()) {
    return;
  }
  try {
    localStorage.setItem(key, JSON.stringify(responses));
  } catch {
    /* best-effort cache */
  }
}
